import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useReduceEffects } from './useReduceEffects.js';

/** A boost frame older than this (server `at` against this clock) is not played. */
export const BOOST_STALE_MS = 12000;
/** At most this many boosts are animated (the running one and those waiting); more snap. */
export const BOOST_MAX_ANIMATED = 5;
/** A boost that has not ended by now (its card left mid-boost) is given up on. */
export const BOOST_TIMEOUT_MS = 8000;
/** Slack for matching a chat line's (whole-second) time against a boost's `at`. */
export const BOOST_LINE_SLACK_MS = 2000;

const readyOf = (fxRef) => (fxRef.current?.status === 'ready' ? fxRef.current : null);

/** Entry ids in rail order: the locked up-next song first, then the rest as given. */
const stripIds = (queue) => {
  const locked = queue.find((e) => e.locked);
  return [...(locked ? [locked.entryId] : []), ...queue.filter((e) => !e.locked).map((e) => e.entryId)];
};

/** What a running boost cannot survive: songs leaving the queue or the up-next song changing. */
const membershipOf = (queue) => ({
  ids: new Set(queue.map((e) => e.entryId)),
  locked: queue.find((e) => e.locked)?.entryId ?? null,
});

/**
 * `ids` with `entryId` at rail spot `spot` (1 = #1, the first behind the
 * locked up-next song, which keeps the front: a boost to the top lands below it).
 */
export const placeAtSpot = (ids, entryId, spot, lockedId = null) => {
  const rest = ids.filter((id) => id !== entryId && id !== lockedId);
  const index = Math.min(Math.max(0, (Number(spot) || 1) - 1), rest.length);
  rest.splice(index, 0, entryId);
  return [...(lockedId != null && ids.includes(lockedId) ? [lockedId] : []), ...rest];
};

/** Whether a boost chat line for `boost.entryId` stamped `timestamp` belongs to a held boost. */
export const isBoostLineHeld = (held, line) => {
  if (!held?.length || !line?.boost) return false;
  const at = Date.parse(line.timestamp) || 0;
  return held.some((h) => h.entryId === line.boost.entryId && at >= h.at - BOOST_LINE_SLACK_MS);
};

/**
 * Boost playback on the stream page's queue (ENG-808, FX-3).
 *
 * Every viewer gets one `fx_boost` frame per confirmed boost (ENG-807):
 * `{entryId, tipId, boostId, vibeId, fromSpot, toSpot, toTop, passedEntryId?, level, at}`.
 * `onBoostFrame` plays it on the boosted song's card with the booster's boost
 * package (`boostId`, fetched when this viewer does not have it):
 * charge → burst → slide → afterglow, the slide being the queue reorder.
 * From the burst on, the song shows the booster's vibe set (`vibeId`,
 * ENG-866): the card switches to it at boost-end (`card.boost(pkg, {vibe})`),
 * so its afterglow, any later build and the #1 color are the booster's. The
 * vibe is also recorded on the stage (`fx.setBoostedVibe`) so a boost that
 * is not animated still leaves the song in the booster's look. In
 * Photosensitivity mode both go through `fx.resolvePackage`, which shows the
 * package's photosensitive alternative or the default instead.
 *
 * - The rail shows `entries`: the server's queue, except while boosts play,
 *   when the order is held here so the boosted card starts at `fromSpot`
 *   (the `queue_update` with the new order lands before the frame) and moves
 *   to `toSpot` when the engine says so, with `engine.flip` at the duration
 *   and easing the package asks for. Positions are renumbered in shown order.
 *   When the last boost ends the hold is let go and the rail shows the
 *   server's order again, so it always ends up matching the server.
 * - One boost plays at a time, in arrival order. With `BOOST_MAX_ANIMATED`
 *   already running or waiting, a new one is not animated: its reorder
 *   simply arrives with the server's order.
 * - Not played (snaps to the server order): a frame older than
 *   `BOOST_STALE_MS` by its server `at`, one arriving in a hidden tab, a song
 *   whose card is not on screen (paged off the rail) or busy, or the
 *   up-next song (the server never boosts it).
 * - A song leaving the queue or the up-next song changing under a running
 *   boost cancels it (`card.cancelBoost()`, no afterglow) and drops those
 *   waiting. A card removed mid-boost never ends its boost, so each boost
 *   gives up after `BOOST_TIMEOUT_MS`.
 * - The locked (#1) look follows `entries` (`useFxAfterglow`, ENG-809): a
 *   boost to the top takes it when the move lands it at #1.
 * - `held` lists the boosts not yet ended ({entryId, at}); the chat holds
 *   their boost line until `boost-end` (`isBoostLineHeld`).
 *
 * Boosts go through `card.boost`, which `useFxStage` wraps so the Reduce
 * effects setting (ENG-810) always applies. `moving` is true while the
 * order is held; the rail's own reorder glide stays off meanwhile.
 *
 * While effects are reduced (`useReduceEffects`: Photosensitivity mode or the
 * device's reduce-motion setting, ENG-837) the slide does not animate: the
 * new order is shown at once and `engine.flip` is skipped (it uses
 * `element.animate`, which the reduced-motion stylesheet cannot reach).
 */
export const useFxBoosts = ({ fx, queue = [] }) => {
  const fxRef = useRef(fx);
  fxRef.current = fx;
  const queueRef = useRef(queue);
  queueRef.current = queue;
  const [order, setOrder] = useState(null);
  const orderRef = useRef(null);
  const jobsRef = useRef([]);
  const runningRef = useRef(null);
  const mountedRef = useRef(true);
  const [held, setHeld] = useState([]);
  const reduceMotion = useReduceEffects();
  const reduceMotionRef = useRef(reduceMotion);
  reduceMotionRef.current = reduceMotion;

  const showOrder = useCallback((next, sync = false) => {
    orderRef.current = next;
    if (!mountedRef.current) return;
    if (sync) flushSync(() => setOrder(next));
    else setOrder(next);
  }, []);

  const hold = useCallback((job) => setHeld((prev) => [...prev, { key: job.key, entryId: job.entryId, at: job.at }]), []);
  const unhold = useCallback((job) => {
    if (mountedRef.current) setHeld((prev) => prev.filter((h) => h.key !== job.key));
  }, []);

  const lockedId = () => queueRef.current.find((e) => e.locked)?.entryId ?? null;

  /** The held order trimmed to the songs still queued, new songs at the back. */
  const currentIds = () => {
    const live = stripIds(queueRef.current);
    const base = (orderRef.current ?? live).filter((id) => live.includes(id));
    live.forEach((id) => { if (!base.includes(id)) base.push(id); });
    return base;
  };

  const pumpRef = useRef(() => {});

  const finish = useCallback((job) => {
    if (job.done) return;
    job.done = true;
    if (runningRef.current === job) runningRef.current = null;
    clearTimeout(job.timer);
    unhold(job);
    // Played or not, the song now wears its booster's look.
    readyOf(fxRef)?.refreshCardVibe?.(job.entryId);
    pumpRef.current();
  }, [unhold]);

  const run = useCallback(async (job) => {
    runningRef.current = job;
    const stale = () => !Number.isFinite(job.at) || Date.now() - job.at > BOOST_STALE_MS;
    let f = readyOf(fxRef);
    if (!f || stale()) { finish(job); return; }
    let pkg = null;
    let vibe;
    try {
      pkg = await f.resolvePackage(job.boostId || f.defaults?.boost || null, 'boost');
      if (job.vibeId) {
        const vibeId = await f.resolvePackage(job.vibeId, 'vibe');
        vibe = vibeId ? f.packageFor(vibeId, 'vibe') ?? undefined : undefined;
      }
    } catch (_e) {
      pkg = pkg ?? null;
    }
    f = readyOf(fxRef);
    const card = f?.getCard(job.entryId) ?? null;
    // Not `card.locked`: the new order is usually in already, so a boost to
    // the top finds its card wearing the #1 look before it plays.
    const upNext = queueRef.current.some((e) => e.entryId === job.entryId && e.locked);
    if (runningRef.current !== job || !f || !card || card.busy || upNext || stale() || !card.el) {
      finish(job);
      return;
    }
    // Start from the old spot: the server's new order may already be in.
    const locked = lockedId();
    showOrder(placeAtSpot(currentIds(), job.entryId, job.fromSpot, locked), true);
    job.members = membershipOf(queueRef.current);
    const target = job.passedEntryId != null ? f.getCard(job.passedEntryId)?.el ?? undefined : undefined;
    const move = (info) => {
      if (runningRef.current !== job) return Promise.resolve();
      const next = placeAtSpot(currentIds(), job.entryId, job.toSpot, lockedId());
      if (reduceMotionRef.current) {
        showOrder(next, true);
        return Promise.resolve();
      }
      const els = () => currentIds().map((id) => f.getCard(id)?.el).filter(Boolean);
      return f.engine.flip(els, () => showOrder(next, true), {
        focus: card.el,
        durationMs: info?.durationMs,
        easing: info?.easing,
      });
    };
    job.card = card;
    let played;
    try {
      played = card.boost(pkg, {
        target, toTop: Boolean(job.toTop), level: job.level, move, ...(vibe !== undefined ? { vibe } : {}),
      });
    } catch (_e) {
      played = Promise.resolve();
    }
    const timeout = new Promise((resolve) => { job.timer = setTimeout(() => resolve('timeout'), BOOST_TIMEOUT_MS); });
    const result = await Promise.race([Promise.resolve(played).catch(() => null), timeout]);
    if (result === 'timeout' && card.busy) {
      try { card.cancelBoost(); } catch (_e) { /* the card is gone */ }
    }
    finish(job);
  }, [finish, showOrder]);

  pumpRef.current = () => {
    if (runningRef.current) return;
    const job = jobsRef.current.shift();
    if (job) {
      run(job);
      return;
    }
    // Nothing left to play: back to the server's order.
    if (orderRef.current) showOrder(null);
  };

  const onBoostFrame = useCallback((frame) => {
    const f = readyOf(fxRef);
    if (!f || !frame || frame.entryId == null) return;
    const at = Date.parse(frame.at);
    if (!Number.isFinite(at) || Date.now() - at > BOOST_STALE_MS) return;
    // The booster's look, whether or not this boost is animated here.
    const boostedVibe = typeof frame.vibeId === 'string' && frame.vibeId ? frame.vibeId : null;
    if (boostedVibe) f.setBoostedVibe?.(frame.entryId, boostedVibe);
    const hidden = typeof document !== 'undefined' && document.hidden;
    const waiting = jobsRef.current.length + (runningRef.current ? 1 : 0);
    if (hidden || waiting >= BOOST_MAX_ANIMATED) {
      if (boostedVibe) f.refreshCardVibe?.(frame.entryId);
      return;
    }
    const job = {
      key: `${frame.tipId ?? ''}:${frame.entryId}:${frame.at}`,
      entryId: frame.entryId,
      boostId: typeof frame.boostId === 'string' ? frame.boostId : null,
      vibeId: boostedVibe,
      fromSpot: Number(frame.fromSpot) || 1,
      toSpot: Number(frame.toSpot) || 1,
      toTop: Boolean(frame.toTop),
      passedEntryId: frame.passedEntryId ?? null,
      level: Math.min(1, Math.max(0, Number(frame.level) || 0)),
      at,
    };
    if (jobsRef.current.some((j) => j.key === job.key) || runningRef.current?.key === job.key) return;
    jobsRef.current.push(job);
    hold(job);
    // Hold the order from now: the boosted card waits at its old spot.
    if (!orderRef.current) showOrder(currentIds());
    pumpRef.current();
  }, [hold, showOrder]);

  // The queue changed under a running boost: songs left or the up-next song
  // moved on. Cancel it and drop those waiting; the server's order shows.
  const membersKey = `${queue.map((e) => e.entryId).join(',')}|${queue.find((e) => e.locked)?.entryId ?? ''}`;
  useEffect(() => {
    const job = runningRef.current;
    if (job?.members) {
      const now = membershipOf(queueRef.current);
      const left = [...job.members.ids].some((id) => !now.ids.has(id));
      if (left || now.locked !== job.members.locked) {
        jobsRef.current.forEach(unhold);
        jobsRef.current = [];
        try { job.card?.cancelBoost(); } catch (_e) { /* the card is gone */ }
        finish(job);
      }
    }
  }, [membersKey, finish, unhold]);

  // The stage going away ends every boost; nothing is held after it.
  const ready = fx?.status === 'ready';
  useEffect(() => {
    if (ready) return;
    jobsRef.current.forEach(unhold);
    jobsRef.current = [];
    const job = runningRef.current;
    if (job) finish(job);
    else if (orderRef.current) showOrder(null);
  }, [ready, finish, unhold, showOrder]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(runningRef.current?.timer);
      jobsRef.current = [];
      runningRef.current = null;
    };
  }, []);

  const entries = useMemo(() => {
    if (!order) return queue;
    const byId = new Map(queue.map((e) => [e.entryId, e]));
    const ids = order.filter((id) => byId.has(id));
    queue.forEach((e) => { if (!ids.includes(e.entryId)) ids.push(e.entryId); });
    let position = 0;
    return ids.map((id) => {
      const entry = byId.get(id);
      if (entry.locked) return entry;
      position += 1;
      return entry.position === position ? entry : { ...entry, position };
    });
  }, [order, queue]);

  return { entries, onBoostFrame, held, moving: order != null };
};

export default useFxBoosts;
